import type { CSSProperties, ReactNode } from 'react';
import { columnSortHint, type ColumnSortKind } from '@/lib/column-sort';
import { Icon } from '@/components/Icon';

/**
 * 눌러서 정렬하는 표 머리 칸(2026-10-01 — 고객사 마스터 · 로봇 목록). 단계(1번 · 2번 · 해제)는
 * lib/column-sort 정본 한 벌. 지금 상태와 다음 상태는 title 이 말한다 — 화살표 모양만으로 뜻을
 * 전하지 않는다. `disabled` 면 평범한 머리 칸이다(가려진 금액 칸 등).
 */
export function SortableTh({
  label,
  kind,
  step,
  disabled = false,
  onToggle,
  testId,
  thStyle,
}: {
  label: ReactNode;
  kind: ColumnSortKind;
  step: 1 | 2 | null;
  disabled?: boolean;
  onToggle: () => void;
  testId?: string;
  thStyle: CSSProperties;
}) {
  if (disabled) return <th style={thStyle}>{label}</th>;
  // 화면 낭독기용 방향 — 글자·숫자·분류의 1번은 오름차순, 날짜의 1번은 내림차순(최신).
  const ariaSort: 'ascending' | 'descending' | 'none' =
    step === null ? 'none' : (kind === 'date') === (step === 1) ? 'descending' : 'ascending';
  // 1번·2번은 위/아래 꺾쇠(오름·내림을 모양으로), 분류는 목록 그림. 뜻은 title 이 글로 말한다.
  const iconName =
    step === null
      ? null
      : kind === 'category'
        ? 'list-check'
        : step === 1
          ? 'chev-up'
          : 'chev-down';
  return (
    <th style={thStyle} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={onToggle}
        title={columnSortHint(kind, step)}
        data-testid={testId}
        data-sort-step={step ?? 0}
        style={{ ...sortThButton, color: step === null ? 'inherit' : 'var(--fg-brand)' }}
      >
        {label}
        {iconName && <Icon name={iconName} size={12} style={{ flexShrink: 0 }} />}
      </button>
    </th>
  );
}

/** 이 칸의 단계가 서버에 보낼 방향 — 날짜만 첫 누름이 내림차순(최신)이다. */
export function columnSortDir(kind: ColumnSortKind, step: 1 | 2): 'asc' | 'desc' {
  const first = kind === 'date' ? 'desc' : 'asc';
  return step === 1 ? first : first === 'asc' ? 'desc' : 'asc';
}

const sortThButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  border: 'none',
  background: 'none',
  padding: 0,
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
